import { useEffect, useRef } from 'react';
import { loadTurnstile, siteKey, turnstileEnabled } from '../../lib/turnstile.js';

/**
 * Renders the Turnstile challenge and reports its token upward.
 *
 * Renders nothing at all when no site key is configured, so local development
 * and the test suite never reach for the network.
 */
export function TurnstileWidget({
  locale,
  onToken,
  onError,
}: {
  locale: string;
  onToken: (token: string | null) => void;
  onError: () => void;
}) {
  const container = useRef<HTMLDivElement>(null);
  // Kept in a ref rather than state: changing it must not re-render, or the
  // widget would be torn down and rebuilt on every token.
  const widgetId = useRef<string | null>(null);

  useEffect(() => {
    if (!turnstileEnabled) return;

    let cancelled = false;
    const target = container.current;
    if (!target) return;

    loadTurnstile()
      .then((turnstile) => {
        if (cancelled) return;
        widgetId.current = turnstile.render(target, {
          sitekey: siteKey,
          language: locale,
          callback: (token) => onToken(token),
          // A token is single-use and expires; drop it so the form cannot
          // submit something the server will reject.
          'expired-callback': () => onToken(null),
          'error-callback': () => {
            onToken(null);
            onError();
          },
        });
      })
      .catch(() => {
        if (!cancelled) onError();
      });

    return () => {
      cancelled = true;
      const id = widgetId.current;
      if (id && window.turnstile) {
        window.turnstile.remove(id);
        widgetId.current = null;
      }
    };
    // Re-rendered when the locale changes so the challenge follows the page.
  }, [locale, onToken, onError]);

  if (!turnstileEnabled) return null;

  return <div ref={container} className="turnstile" />;
}

/** Clears the current challenge so a fresh token can be issued. */
export function resetTurnstile(): void {
  window.turnstile?.reset();
}
